// (웹/앱) 마이 링글 > AI 분석 통계 - 내가 집중하고 싶은 학습 영역

import { BUTTON_SIZE, DSButton } from '@repo/design-system/index'
import { useCallback, useEffect } from 'react'

import { useCafSummaryHighlightsV5 } from '../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { getRoundValue } from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import useMapTagToWording from '../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import useRNMsg from '../../../../modules/hooks/webview/common/useRNMsg'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import LoadingSpinner from '../../../common/LoadingSpinner'
import useSummaryHighlightEditPopup from '../../../student/portal/lessons/caf/CAFOverview/popup/useSummaryHighlightEditPopup'
import HighSumCardTemplate from '../highlight/HighSumCardTemplate'
import SimpleBarChart from './SimpleBarChart'

const PinnedHighlightSection = ({ startDate, endDate }) => {
  const {
    data: dataList,
    refetch,
    isLoading,
  } = useCafSummaryHighlightsV5({
    startDate,
    endDate,
    pinned: true,
    recommended: false,
  })
  const { showEditPopup, setShowEditPopup, HighlightEditPopupComponent } = useSummaryHighlightEditPopup(
    startDate,
    endDate
  )
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const router = useAppRouter()
  const { receivePostMsg, removeListener } = useRNMsg()
  const { navigate } = useWebviewNavigation()

  const { t } = useTranslation()
  const { countWordingPerTag, namePerTag, iconPerTag } = useMapTagToWording()
  const shownCount = useCallback((item) => item?.average_count ?? item?.count, [])
  const handleClickCard = (tagId, startDate, endDate) => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightDetailActivity, {
        tagId: tagId,
        startDate,
        endDate,
      })
    } else if (isMobile) {
      const url = urls.webview.caf.statistic.highlight.tag(tagId, startDate, endDate)
      navigate({ url })
    } else {
      router.push(urls.student.newPortal.myRingle.AIAnalysis.asTag(2, startDate, endDate, tagId))
    }
  }

  const goToHighlightEditPage = (start, end) => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightListActivity, {
        initialStartDate: startDate,
        initialEndDate: endDate,
        edit: true,
      })
    } else if (isMobile) {
      const url = urls.webview.caf.statistic.highlight.list(start, end, true)
      navigate({ url })
    } else {
      router.push(urls.student.newPortal.myRingle.AIAnalysis.asEditWithDate(router.query?.tab || 0, startDate, endDate))
    }
  }
  const goHighlightListPage = () => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightListActivity, {
        initialStartDate: startDate,
        initialEndDate: endDate,
        edit: true,
      })
    } else {
      router.push(urls.student.newPortal.myRingle.AIAnalysis.asTab(2))
    }
  }

  const summGraphData = useCallback((highlights) => {
    return highlights?.slice(0, 14).map((item, idx) => {
      return {
        ...item,
        color: COLORS.GRAY300,
        turn: idx,
      }
    })
  }, [])
  useEffect(() => {
    const callback = (msg) => {
      if (msg === 'focus') {
        refetch()
      }
    }
    receivePostMsg((message) => callback(message))
    return () => {
      removeListener(callback)
    }
  }, [receivePostMsg, refetch, removeListener])

  if (isLoading || !dataList)
    return (
      <div className="flex h-[100px] w-full items-center justify-center">
        <LoadingSpinner show primary />
      </div>
    )

  if (isMobile) {
    return (
      <section className="w-full py-[24px]">
        <div className={styles.title.box}>
          <div className={styles.title.text}>{t(n_caf.overview.tophighlights_title)}</div>
          {dataList.length > 0 && (
            <div className={styles.title.button} onClick={() => goToHighlightEditPage(startDate, endDate)}>
              {t(n_caf.overview.tophighlights_edit)}
            </div>
          )}
        </div>
        {dataList.length > 0 ? (
          <ul>
            {dataList.map((highItem, index) => {
              const Icon = iconPerTag(highItem.tag)
              return (
                <li key={index + 'pinned_highlight'}>
                  <HighSumCardTemplate
                    title={
                      <p className={styles.highItem.text}>
                        <Icon
                          width={18}
                          height={18}
                          className="mr-[2px] [&>*]:fill-current"
                          color={COLORS.PURPLE500}
                          fill={COLORS.PURPLE500}
                        />
                        {namePerTag(highItem.tag)}
                      </p>
                    }
                    subtitle={
                      shownCount(highItem) > 0
                        ? t(countWordingPerTag(highItem.tag, shownCount(highItem)).count)
                        : t(countWordingPerTag(highItem.tag).noCount)
                    }
                    bottomSection={
                      shownCount(highItem) !== 0 ? (
                        <SimpleBarChart
                          data={summGraphData(highItem.counts)}
                          graphKey={'count'}
                          leftCount={<span className="!text-purple-500">{getRoundValue(highItem.average_count)}</span>}
                          rightCount={null}
                          marker={highItem.average_count}
                        />
                      ) : null
                    }
                    disabled={shownCount(highItem) === 0}
                    onClick={() => handleClickCard(highItem.tag, startDate, endDate)}
                  />
                </li>
              )
            })}
          </ul>
        ) : (
          <div className="text-h5 relative flex flex-col items-center justify-center rounded-[6px] bg-gray-100 py-[16px] text-center font-medium text-purple-500">
            <div>{t(n_caf.overview.tophighlights_info)}</div>
            <div className="mt-[12px]">
              <DSButton
                size={BUTTON_SIZE.SM}
                customBgColor="white"
                className="!w-auto border-[1.5px] px-[16px] py-[8px]"
                customBorderColor={COLORS.PURPLE500}
                onClick={(e) => goToHighlightEditPage}
              >
                <DSButton.Label customTxtColor={COLORS.PURPLE500} className="text-center">
                  {t(n_caf.overview.checkout.btn)}
                </DSButton.Label>
              </DSButton>
            </div>
          </div>
        )}
      </section>
    )
  } else {
    return (
      <section className="w-full">
        <div className={styles.title.box_pc}>
          <div className="mb-[8px] flex w-full items-center justify-between">
            <div className={styles.title.text_pc}>{t(n_caf.overview.tophighlights_title)}</div>
            {dataList.length > 0 && (
              <div
                className="flex cursor-pointer flex-row items-center"
                onClick={() => goToHighlightEditPage(startDate, endDate)}
              >
                <div className="mr-4 text-[16px] font-bold text-black">{t(n_caf.highlight_list.edit)}</div>
              </div>
            )}
          </div>
        </div>
        {dataList.length > 0 && <div className={styles.title.subtitle_pc}>{t(n_caf.overview.tophighlights_info1)}</div>}
        {dataList.length > 0 ? (
          <div className="mt-[24px] flex flex-wrap gap-[16px]">
            {dataList.map((highItem, index) => {
              const Icon = iconPerTag(highItem.tag)
              return (
                <HighSumCardTemplate
                  key={index + 'pinned_highlight'}
                  title={
                    <p className={styles.highItem.text}>
                      <Icon
                        width={18}
                        height={18}
                        className="mr-[2px] [&>*]:fill-current"
                        color={COLORS.PURPLE500}
                        fill={COLORS.PURPLE500}
                      />
                      {namePerTag(highItem.tag)}
                    </p>
                  }
                  subtitle={
                    shownCount(highItem) > 0
                      ? t(countWordingPerTag(highItem.tag, shownCount(highItem)).count)
                      : t(countWordingPerTag(highItem.tag).noCount)
                  }
                  bottomSection={
                    shownCount(highItem) !== 0 ? (
                      <SimpleBarChart
                        data={summGraphData(highItem.counts)}
                        graphKey={'count'}
                        leftCount={<span className="!text-purple-500">{getRoundValue(highItem.average_count)}</span>}
                        rightCount={null}
                        marker={highItem.average_count}
                      />
                    ) : null
                  }
                  disabled={shownCount(highItem) === 0}
                  onClick={() => handleClickCard(highItem.tag, startDate, endDate)}
                  className="h-[188px] w-[336px]"
                />
              )
            })}
          </div>
        ) : (
          <div className="text-h5 mt-[12px] mb-[24px] flex flex-col items-center justify-between rounded-[6px] bg-gray-100 p-[16px] text-center font-medium text-purple-500">
            <div className="text-[16px] text-purple-500">{t(n_caf.overview.tophighlights_info1)}</div>
            <div className="mt-[12px]">
              <DSButton
                customBorderColor={COLORS.PURPLE600}
                customBgColor="white"
                className="border-[1.5px] px-[16px] py-[8px]"
                onClick={goHighlightListPage}
              >
                <DSButton.Label customTxtColor={COLORS.PURPLE600} className="text-center">
                  {t(n_caf.overview.checkout.btn)}
                </DSButton.Label>
              </DSButton>
            </div>
          </div>
        )}
        <HighlightEditPopupComponent />
      </section>
    )
  }
}

export default PinnedHighlightSection

const styles = {
  title: {
    box: `mb-[12px] flex items-center justify-between`,
    box_pc: `flex items-center justify-between`,
    text_pc: `text-[20px] font-bold text-black`,
    text: `text-sTitle font-bold text-black`,
    button: `Body2-14Bd text-gray-500`,
    subtitle_pc: `text-[16px] font-medium text-gray-600`,
  },
  placecholder: {
    box: `flex items-start justify-center gap-[4px] rounded border border-purple-50 bg-purple-500 bg-opacity-10 py-[16px] px-[20px] text-center`,
    title: `text-h5 keep-all font-medium leading-[20px] tracking-tight text-purple-500`,
  },
  highItem: { text: `text-h5 flex gap-4 font-medium text-purple-500` },
}
